import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useMenuStore } from '@/stores/menuStore'
import { Button } from '@/components/ui'
import CategoryForm from '@/components/CategoryForm'
import ProductList from '@/components/ProductList'
import ProductForm from '@/components/ProductForm'
import type { Category, Product } from '@/types'
import { Plus, Edit2, Trash2, Sparkles } from 'lucide-react'

export default function MenuPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const {
    categories,
    products,
    selectedCategory,
    isLoading,
    fetchCategories,
    fetchProducts,
    setSelectedCategory,
    deleteCategory,
  } = useMenuStore()

  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [showProductForm, setShowProductForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)

  const restaurantId = user?.restaurantId

  // Load data on mount
  useEffect(() => {
    if (restaurantId) {
      fetchCategories(restaurantId)
      fetchProducts(restaurantId)
    }
  }, [restaurantId])

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category)
    setShowCategoryForm(true)
  }

  const handleDeleteCategory = async (category: Category) => {
    if (window.confirm(`Sei sicuro di voler eliminare la categoria "${category.name}"?\nTutti i prodotti in questa categoria verranno eliminati.`)) {
      try {
        await deleteCategory(category.id)
        if (selectedCategory?.id === category.id) {
          setSelectedCategory(null)
        }
      } catch (error) {
        // Error handled by store
      }
    }
  }

  const handleAddProduct = () => {
    setEditingProduct(null)
    setShowProductForm(true)
  }

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product)
    setShowProductForm(true)
  }

  const handleCloseCategoryForm = () => {
    setShowCategoryForm(false)
    setEditingCategory(null)
  }

  const handleCloseProductForm = () => {
    setShowProductForm(false)
    setEditingProduct(null)
  }

  // Filter products by selected category
  const filteredProducts = selectedCategory
    ? products.filter(p => p.categoryId === selectedCategory.id)
    : products

  if (!restaurantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-white/40">Nessun ristorante associato</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 bg-gray-100 dark:bg-[#1a1a1f] min-h-screen">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-white">Gestione Menu</h1>
          <p className="text-gray-700 dark:text-white/60 mt-2">Organizza le tue categorie e prodotti</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/menu/import')}
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 flex items-center gap-2 shadow-lg"
        >
          <Sparkles size={20} />
          Importa Menu con AI
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Categories Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-[#25252d] rounded-2xl shadow-sm border border-gray-300 dark:border-white/[0.08] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-gray-900 dark:text-white">Categorie</h2>
              <Button
                onClick={() => {
                  setEditingCategory(null)
                  setShowCategoryForm(true)
                }}
                variant="primary"
                size="sm"
              >
                <Plus size={16} />
                Aggiungi
              </Button>
            </div>

            {isLoading && categories.length === 0 ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-orange-500 border-t-transparent mb-2"></div>
                <p className="text-gray-700 dark:text-white/60">Caricamento...</p>
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-8 text-gray-700 dark:text-white/60">
                <div className="text-4xl mb-2">📋</div>
                <p className="mb-2 font-medium">Nessuna categoria</p>
                <p className="text-sm text-gray-500 dark:text-white/40">Inizia creando una categoria</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* All Products Button */}
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition ${
                    selectedCategory === null
                      ? 'bg-orange-500 text-white font-medium shadow-sm'
                      : 'hover:bg-gray-100 dark:hover:bg-white/[0.04] text-gray-700 dark:text-white/70'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>📋 Tutti i prodotti</span>
                    <span className={`text-sm ${selectedCategory === null ? 'text-white' : 'text-gray-500 dark:text-white/40'}`}>
                      {products.length}
                    </span>
                  </div>
                </button>

                {/* Category List */}
                {categories.map((category) => {
                  const categoryProducts = products.filter(p => p.categoryId === category.id)
                  const isSelected = selectedCategory?.id === category.id

                  return (
                    <div
                      key={category.id}
                      className={`rounded-xl border-2 transition ${
                        isSelected
                          ? 'border-orange-500/50 bg-orange-500/10'
                          : 'border-gray-200 dark:border-white/[0.08] hover:border-orange-500/30 bg-white dark:bg-white/[0.02]'
                      }`}
                    >
                      <button
                        onClick={() => setSelectedCategory(category)}
                        className="w-full text-left px-4 py-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {category.icon && <span className="text-xl">{category.icon}</span>}
                            <span className={`font-medium ${isSelected ? 'text-orange-500 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
                              {category.name}
                            </span>
                          </div>
                          <span className={`text-sm ${isSelected ? 'text-orange-500 dark:text-orange-400' : 'text-gray-500 dark:text-white/40'}`}>
                            {categoryProducts.length}
                          </span>
                        </div>
                      </button>

                      {/* Category Actions */}
                      <div className="px-4 pb-2 flex gap-2">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition"
                        >
                          <Edit2 size={14} />
                          Modifica
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category)}
                          className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:opacity-80 transition"
                        >
                          <Trash2 size={14} />
                          Elimina
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Products Section */}
        <div className="lg:col-span-2">
          <ProductList
            products={filteredProducts}
            selectedCategory={selectedCategory}
            onAddProduct={handleAddProduct}
            onEditProduct={handleEditProduct}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Modals */}
      {showCategoryForm && (
        <CategoryForm
          category={editingCategory}
          restaurantId={restaurantId}
          onClose={handleCloseCategoryForm}
        />
      )}

      {showProductForm && (
        <ProductForm
          product={editingProduct}
          restaurantId={restaurantId}
          categories={categories}
          onClose={handleCloseProductForm}
        />
      )}
    </div>
  )
}
